import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';

import { AuthService } from '../../services/auth.service';

/**
 * Session Expired page.
 *
 * Shown when the user's session cannot be silently refreshed (the BFF
 * refresh_token cookie is gone/expired). Unlike /unauthorized (which is for
 * insufficient permissions on a still-valid session), this page communicates
 * that the session simply timed out and offers a single re-login action that
 * is visually consistent with the initial login screen.
 *
 * The URL the user was working on is preserved in sessionStorage
 * (`auth_return_url`) so that after re-authentication they are taken back to
 * exactly where they left off (unsaved form data cannot be recovered).
 */
@Component({
  selector: 'app-session-expired',
  standalone: true,
  imports: [CommonModule, ButtonModule, DividerModule],
  template: `
    <div
      class="session-expired-container flex flex-column justify-content-center align-items-center h-screen bg-gray-50"
    >
      <div
        class="session-expired-box bg-white p-5 shadow-2 border-round-xl border-1 border-300 w-full max-w-28rem text-center"
      >
        <div class="mb-3">
          <i class="pi pi-clock" style="font-size: 3rem; color: #ffa502;"></i>
        </div>

        <h4 class="text-xl font-bold mt-0 mb-3">Sessione scaduta</h4>

        <p class="text-600 mb-2">
          La tua sessione è scaduta per inattività o per superamento del tempo massimo.
        </p>
        <p class="text-500 text-sm mb-4">
          Effettua nuovamente il login per continuare. Verrai riportato
          automaticamente alla pagina su cui stavi lavorando.
        </p>

        <p-button
          label="Accedi di nuovo"
          icon="pi pi-sign-in"
          (onClick)="reLogin()"
          [loading]="isLoading()"
          styleClass="w-full p-button-lg"
          severity="info"
        >
        </p-button>

        <small class="block text-center text-500 mt-3">
          <i class="pi pi-shield mr-1"></i>
          Autenticazione sicura tramite Azure Active Directory
        </small>

        @if (returnUrl()) {
          <p-divider></p-divider>
          <small class="block text-400 text-xs">
            <i class="pi pi-info-circle mr-1"></i>
            Eventuali dati non salvati nel modulo non potranno essere recuperati.
          </small>
        }
      </div>
    </div>
  `,
})
export class SessionExpiredComponent implements OnInit {
  private authService = inject(AuthService);
  private titleService = inject(Title);

  isLoading = signal(false);
  returnUrl = signal<string | null>(null);

  ngOnInit(): void {
    this.titleService.setTitle('Sessione Scaduta - Portale Fiduciari AXA');

    const storedReturnUrl = sessionStorage.getItem('auth_return_url');
    this.returnUrl.set(storedReturnUrl);

    console.log('⏰ [SessionExpired] Page shown. returnUrl:', storedReturnUrl);
  }

  /**
   * Restart the interactive login flow, preserving the URL the user was on so
   * that redirectAfterAuthentication() takes them back after login.
   */
  reLogin(): void {
    this.isLoading.set(true);

    const returnUrl =
      this.returnUrl() ?? sessionStorage.getItem('auth_return_url') ?? undefined;

    console.log('🔐 [SessionExpired] Re-login requested. returnUrl:', returnUrl);

    // login() stores the returnUrl in sessionStorage and redirects to Azure AD.
    void this.authService.login(returnUrl);
  }
}
